import { buildClaimUnlockPayload, type TrialDto } from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAnalytics } from "@/contexts/analytics-context";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import type { FftClaimEventProps } from "@/lib/analytics";
import { Logger } from "@/utils/logger";

import { readDexSdk } from "../../dex-sdk";
import { activeTrialQueryKey, writeActiveTrial } from "../../queries/funded-trial/active-trial";
import { fundedTrialEligibilityQueryKey } from "../../queries/funded-trial/eligibility";
import { usdcBalanceQueryKey } from "../../queries/usdc-balance";
import { usePrimarySessionKeyDex, useWriteDexSdk } from "../../write-dex-sdk-context";
import { useWalletFeePayerWithFallback } from "../wallet-fee-payer";
import { requireCampaignAddrs } from "./campaign-addrs";

export interface ClaimUnlockArgs {
  /** On-chain `lock_id` (u64). */
  lockId: bigint;
  /** Analytics-only. */
  lockedAmountUsd: number;
  lockDurationDays: number;
  claimDelayMs?: number;
}

// bigint lockId → string at the boundary: rudder payloads are JSON-serialized.
function toClaimEventProps(args: ClaimUnlockArgs): FftClaimEventProps {
  return {
    lockId: args.lockId.toString(),
    lockedAmountUsd: args.lockedAmountUsd,
    lockDurationDays: args.lockDurationDays,
    claimDelayMs: args.claimDelayMs,
  };
}

/**
 * Claim an expired lock — returns the user's USDC + any PnL accumulated on
 * locked-fund trading.
 *
 * Signed silently by the primary-subaccount session key
 * when one exists; wallet prompt otherwise. Funds route to the owner on-chain
 * regardless of signer.
 */
export function useClaimUnlock() {
  const queryClient = useQueryClient();
  const { accountAddress } = useDecibelWallet();
  const { primarySubaccountAddress } = useWriteDexSdk();
  const primaryDex = usePrimarySessionKeyDex();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const analytics = useAnalytics();

  return useMutation({
    mutationFn: async (args: ClaimUnlockArgs) => {
      analytics.fft.claimInitiated(toClaimEventProps(args));

      if (!accountAddress) {
        throw new Error("Wallet not connected");
      }

      if (primaryDex) {
        try {
          const committed = await primaryDex.claimFftUnlock({
            ...requireCampaignAddrs(),
            lockId: args.lockId,
            owner: accountAddress,
          });
          return { txHash: committed.hash };
        } catch (error) {
          Logger.warn("Session-key claim-unlock failed, falling back to wallet", {
            extra: { error },
          });
        }
      }

      const data = buildClaimUnlockPayload({
        ...requireCampaignAddrs(),
        lockId: args.lockId,
        owner: accountAddress,
      });

      const pending = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({ transactionHash: pending.hash });
      return { txHash: pending.hash };
    },
    onSuccess: ({ txHash }, args) => {
      analytics.fft.claimSucceeded({ ...toClaimEventProps(args), txHash });
      if (!accountAddress) return;
      const cur = queryClient.getQueryData<TrialDto | null>(
        activeTrialQueryKey({ account: accountAddress }),
      );
      // A running trial outlives its lock; only a spent row is cleared.
      if (cur?.status !== "Active") {
        writeActiveTrial(queryClient, accountAddress, null, { clear: true });
      }
      void queryClient.invalidateQueries({
        queryKey: fundedTrialEligibilityQueryKey({ account: accountAddress }),
      });
      void queryClient.invalidateQueries({
        queryKey: usdcBalanceQueryKey({ accountAddr: accountAddress }),
      });
      if (primarySubaccountAddress) {
        void queryClient.invalidateQueries({
          queryKey: ["accountOverview", primarySubaccountAddress],
        });
      }
    },
    onError: (error, args) => {
      analytics.fft.claimFailed({ ...toClaimEventProps(args), error });
      Logger.error(error, {
        tags: { "fft.mutation": "claim_unlock" },
        extra: toClaimEventProps(args),
      });
    },
  });
}
